import { BodyText, Heading, Modal } from '@farol-ds/react'
import { Image } from '@jusbrasil-web/shared-next-image'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { useEffect, useRef, useState } from 'react'

import validatingImgSrc from '../../../assets/id-document-validation/validation.svg'
import {
  GA4EventHandlers,
  INPUT_SUBMIT_EVENT_COMPONENT_PROPS,
  useValidateStudentEnrollmentDocument,
} from '../../../hooks'
import { clearEnrollmentDocumentFromSharedData, DEFAULT_COURSE } from '../constants'
import { useStudentEnrollmentValidationContext } from '../context'
import { Steps } from '../enums'
import { StudentEnrollmentRejectionReason } from '../types'
import styles from './steps.module.scss'

interface ValidatingEnrollmentProps {
  gA4EventHandlers?: GA4EventHandlers
}

export function ValidatingEnrollment(props: ValidatingEnrollmentProps) {
  const { gA4EventHandlers } = props
  const { setCurrentStep, sharedData, setSharedData, viewer, eventData, onAfterValidationSuccess } =
    useStudentEnrollmentValidationContext()
  const validateEnrollmentDocument = useValidateStudentEnrollmentDocument()
  const [loading, setLoading] = useState(false)
  const hasSubmittedRef = useRef(false)
  const pid = viewer?.pid

  useEffect(() => {
    sendMetrics('UserAccount.StudentEnrollmentProofValidatingViewed', `${pid}`, { ...eventData })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const {
    institution,
    enrollmentPeriod,
    documentData,
    documentName,
    mimeType,
    course = DEFAULT_COURSE,
  } = sharedData

  useEffect(() => {
    if (
      loading ||
      hasSubmittedRef.current ||
      !institution ||
      !enrollmentPeriod ||
      !documentData ||
      !documentName
    ) {
      return
    }

    hasSubmittedRef.current = true
    setLoading(true)

    validateEnrollmentDocument({
      institution,
      course,
      enrollmentPeriod,
      documentData,
      documentName,
      mimeType,
    })
      .then(async (result) => {
        const status = result?.validateStudentEnrollmentDocument?.status
        const rejectionReason = result?.validateStudentEnrollmentDocument?.rejectionReason as
          | StudentEnrollmentRejectionReason
          | null
          | undefined

        if (status === 'enrollment_verified') {
          gA4EventHandlers?.sendInputSubmitComponent({
            ...INPUT_SUBMIT_EVENT_COMPONENT_PROPS.STUDENT_ENROLLMENT_DOCUMENT_SUBMIT,
            successfulAttempt: true,
          })

          try {
            await onAfterValidationSuccess?.()
          } catch (error) {
            console.error('Error running onAfterValidationSuccess:', error)
          }

          setSharedData((prev) => clearEnrollmentDocumentFromSharedData(prev))
          setCurrentStep(Steps.ENROLLMENT_SUCCESS)
          return
        }

        gA4EventHandlers?.sendInputSubmitComponent({
          ...INPUT_SUBMIT_EVENT_COMPONENT_PROPS.STUDENT_ENROLLMENT_DOCUMENT_SUBMIT,
          successfulAttempt: false,
        })

        setSharedData((prev) =>
          clearEnrollmentDocumentFromSharedData({
            ...prev,
            rejectionReason: rejectionReason ?? 'DOCUMENT_UNREADABLE',
          })
        )
        setCurrentStep(Steps.ENROLLMENT_ERROR)
      })
      .catch(() => {
        gA4EventHandlers?.sendInputSubmitComponent({
          ...INPUT_SUBMIT_EVENT_COMPONENT_PROPS.STUDENT_ENROLLMENT_DOCUMENT_SUBMIT,
          successfulAttempt: false,
        })
        setSharedData((prev) =>
          clearEnrollmentDocumentFromSharedData({
            ...prev,
            rejectionReason: 'DOCUMENT_UNREADABLE',
          })
        )
        setCurrentStep(Steps.ENROLLMENT_ERROR)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [
    institution,
    enrollmentPeriod,
    documentData,
    documentName,
    mimeType,
    course,
    loading,
    validateEnrollmentDocument,
    setCurrentStep,
    setSharedData,
    gA4EventHandlers,
    onAfterValidationSuccess,
  ])

  return (
    <Modal.Body className={styles.body} data-testid="validating-enrollment-step">
      <Image
        alt="Verificando documento..."
        src={validatingImgSrc}
        height={200}
        width={200}
        className={styles.image}
      />
      <Heading size="lg" className={styles.title}>
        Verificando documento...
      </Heading>
      <BodyText size="lg" className={styles.text}>
        Aguarde enquanto validamos as informações.
      </BodyText>
    </Modal.Body>
  )
}
